import React from 'react';
import { Mail, Phone, Linkedin, Globe, MapPin, Clock, MessageCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const ContactSection = () => {
  const contactMethods = [
    {
      icon: Mail,
      title: "Email Us",
      value: "info@knowledgeninjalabs.com",
      href: "mailto:info@knowledgeninjalabs.com",
      description: "Send us your queries anytime",
      color: "from-[#5f9dff] to-[#4a8ae8]"
    },
    {
      icon: Phone,
      title: "Call Us",
      value: "+91 9266849651",
      href: "tel:+919266849651",
      description: "Available during business hours",
      color: "from-[#61525a] to-[#4a3f47]"
    },
    {
      icon: Linkedin,
      title: "LinkedIn",
      value: "Connect with us",
      href: "https://www.linkedin.com/in/knowledge-ninja-0b24b8372/",
      description: "Professional networking & updates",
      color: "from-[#0077b5] to-[#005885]"
    },
    {
      icon: Globe,
      title: "Medium Blog",
      value: "Read our insights",
      href: "https://medium.com/@info.knowledgeninjalab",
      description: "Industry insights & thought leadership",
      color: "from-[#00ab6c] to-[#008552]"
    }
  ];

  const businessHours = [
    { day: "Monday - Friday", hours: "9:00 AM - 6:00 PM IST" },
    { day: "Saturday", hours: "10:00 AM - 4:00 PM IST" },
    { day: "Sunday", hours: "Closed" }
  ];

  const scrollToBooking = () => {
    document.getElementById('booking-form').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-[#f7f5f2] via-white to-[#f7f5f2]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e1919] mb-6 font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
            Get in 
            <span className="bg-gradient-to-r from-[#5f9dff] to-[#61525a] bg-clip-text text-transparent">Touch</span>
          </h2>
          <p className="text-lg text-[#736c64] max-w-3xl mx-auto leading-relaxed">
            Ready to start your project? We're here to help you achieve your goals. Reach out to us through any of the channels below.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Methods */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {contactMethods.map((method, index) => {
                const IconComponent = method.icon;
                return (
                  <Card 
                    key={index} 
                    className="group hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 border-0 bg-white/80 backdrop-blur-sm cursor-pointer"
                    onClick={() => window.open(method.href, '_blank')}
                  >
                    <CardContent className="p-6">
                      <div className={`inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br ${method.color} items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-[#1e1919] mb-2 group-hover:text-[#61525a] transition-colors duration-300 font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
                        {method.title}
                      </h3>
                      <p className="text-[#5f9dff] font-semibold mb-1">{method.value}</p>
                      <p className="text-[#736c64] text-sm">{method.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* WhatsApp Direct Connect */}
            <div className="mb-12">
              <Card className="bg-gradient-to-r from-[#25d366]/10 to-[#128c7e]/10 backdrop-blur-sm border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#25d366] to-[#128c7e] flex items-center justify-center shadow-lg">
                      <MessageCircle className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-[#1e1919] mb-2 font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
                    Quick WhatsApp Connect
                  </h3>
                  <p className="text-[#736c64] mb-6">Get instant responses to your queries</p>
                  <Button
                    onClick={() => window.open('https://wa.me/message/RWI6VRIA4IHIJ1', '_blank')}
                    className="bg-gradient-to-r from-[#25d366] to-[#128c7e] hover:from-[#1ea952] hover:to-[#0e6b5c] text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Message Knowledge Ninja on WhatsApp
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* CTA Section */}
            <Card className="bg-gradient-to-r from-[#5f9dff]/10 to-[#61525a]/10 backdrop-blur-sm border-0">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold text-[#1e1919] mb-4 font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
                  Ready to Get Started?
                </h3>
                <p className="text-[#736c64] mb-6 max-w-2xl mx-auto">
                  Book a free consultation to discuss your project requirements and discover how we can help you achieve your goals.
                </p>
                <Button 
                  onClick={scrollToBooking}
                  className="bg-[#61525a] hover:bg-[#4a3f47] text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                >
                  Book Free Consultation
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Business Hours & Location */}
          <div className="space-y-6">
            {/* Business Hours */}
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-[#1e1919] font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif] flex items-center">
                  <Clock className="w-6 h-6 mr-2 text-[#5f9dff]" />
                  Business Hours
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {businessHours.map((schedule, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                      <span className="text-[#61525a] font-medium">{schedule.day}</span>
                      <span className="text-[#736c64]">{schedule.hours}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Location */}
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-[#1e1919] font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif] flex items-center">
                  <MapPin className="w-6 h-6 mr-2 text-[#5f9dff]" />
                  Our Network
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-[#61525a] mb-2">IIT Alumni Network</h4>
                    <p className="text-[#736c64] text-sm leading-relaxed">
                      Based across India, our team of IIT alumni brings expertise from various locations to serve clients globally.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#61525a] mb-2">Contact Information</h4>
                    <p className="text-[#736c64] text-sm leading-relaxed mb-3">
                      Email: info@knowledgeninjalabs.com
                    </p>
                    <p className="text-[#736c64] text-sm leading-relaxed mb-3">
                      Phone: +91 9266849651
                    </p>
                    <p className="text-[#736c64] text-sm leading-relaxed">
                      WhatsApp: Available for instant support
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card className="bg-gradient-to-br from-[#61525a] to-[#4a3f47] text-white border-0 shadow-xl">
              <CardContent className="p-6 text-center">
                <h4 className="text-lg font-bold mb-4 font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
                  Why Choose Us?
                </h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span>Response Time:</span>
                    <span className="font-semibold">&lt; 24 hours</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Success Rate:</span>
                    <span className="font-semibold">98%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Client Satisfaction:</span>
                    <span className="font-semibold">5/5 stars</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export { ContactSection };